// quickCommandsConfig.js
// Configuration for Quick Commands feature - defines all command types and their properties

import { format } from 'date-fns';

// Command status constants
export const COMMAND_STATUS = {
  PENDING: 'pending',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
  ERROR: 'error',
};

// Command type definitions
export const COMMAND_TYPES = {
  SP: {
    code: 'SP',
    label: 'תשלום לספק',
    labelEn: 'Supplier Payment',
    color: 'secondary',
    icon: 'Payment',
    category: 'supplier',
    requiresSupplier: true,
    endpoint: '/supplier/payment/',
    fields: [
      { name: 'supplier_id', type: 'supplier', required: true, label: 'ספק' },
      { name: 'amount', type: 'number', required: true, label: 'סכום' },
      { name: 'actual_date', type: 'date', required: true, label: 'תאריך', default: () => new Date() },
      { name: 'invoice_number', type: 'text', required: false, label: 'חשבונית' },
      { name: 'note', type: 'text', required: false, label: 'הערה' },
    ],
    buildPayload: (data) => ({
      supplier_id: data.supplier_id,
      amount: parseFloat(data.amount),
      actual_date: data.actual_date instanceof Date
        ? data.actual_date.toISOString()
        : new Date(data.actual_date).toISOString(),
      note: data.note || '',
      invoice_number: data.invoice_number || '',
    }),
    queryKeysToInvalidate: ['cashflow', 'suppliers', 'supplier-transactions'],
  },

  SPU: {
    code: 'SPU',
    label: 'רכישה מספק',
    labelEn: 'Supplier Purchase',
    color: 'secondary',
    icon: 'ShoppingCart',
    category: 'supplier',
    requiresSupplier: true,
    endpoint: '/supplier/purchase/log/',
    fields: [
      { name: 'supplier_id', type: 'supplier', required: true, label: 'ספק' },
      { name: 'total_amount', type: 'number', required: true, label: 'סכום כולל' },
      { name: 'actual_date', type: 'date', required: true, label: 'תאריך', default: () => new Date() },
      { name: 'shipping_document', type: 'text', required: false, label: 'תעודת משלוח' },
      { name: 'note', type: 'text', required: false, label: 'הערה' },
    ],
    buildPayload: (data) => ({
      supplier_id: data.supplier_id,
      actual_date: data.actual_date instanceof Date
        ? data.actual_date.toISOString()
        : new Date(data.actual_date).toISOString(),
      subtotal: parseFloat(data.total_amount),
      is_taxable: false,
      is_tax_included: false,
      tax_rate: 0,
      tax_amount: 0,
      net_amount: parseFloat(data.total_amount),
      shipping_cost: 0,
      total_amount: parseFloat(data.total_amount),
      shipping_document: data.shipping_document || '',
      notes: data.note || '',
    }),
    queryKeysToInvalidate: ['cashflow', 'suppliers', 'supplier-transactions', 'supplier-purchases'],
  },

  GE: {
    code: 'GE',
    label: 'הוצאה כללית',
    labelEn: 'General Expense',
    color: 'error',
    icon: 'RemoveCircle',
    category: 'general',
    requiresSupplier: false,
    endpoint: '/general-transactions/create/',
    fields: [
      { name: 'amount', type: 'number', required: true, label: 'סכום' },
      { name: 'actual_date', type: 'date', required: true, label: 'תאריך', default: () => new Date() },
      { name: 'tag_id', type: 'tag', tagType: 'expense', required: false, label: 'קטגוריה' },
      { name: 'note', type: 'text', required: false, label: 'הערה' },
    ],
    buildPayload: (data) => ({
      type: 'expense',
      tag_id: data.tag_id || null,
      amount: parseFloat(data.amount),
      actual_date: data.actual_date instanceof Date
        ? data.actual_date.toISOString()
        : new Date(data.actual_date).toISOString(),
      note: data.note || '',
      payment_methods: [],
    }),
    queryKeysToInvalidate: ['cashflow', 'general-transactions'],
  },

  GI: {
    code: 'GI',
    label: 'הכנסה כללית',
    labelEn: 'General Income',
    color: 'success',
    icon: 'AddCircle',
    category: 'general',
    requiresSupplier: false,
    endpoint: '/general-transactions/create/',
    fields: [
      { name: 'amount', type: 'number', required: true, label: 'סכום' },
      { name: 'actual_date', type: 'date', required: true, label: 'תאריך', default: () => new Date() },
      { name: 'tag_id', type: 'tag', tagType: 'income', required: false, label: 'קטגוריה' },
      { name: 'note', type: 'text', required: false, label: 'הערה' },
    ],
    buildPayload: (data) => ({
      type: 'income',
      tag_id: data.tag_id || null,
      amount: parseFloat(data.amount),
      actual_date: data.actual_date instanceof Date
        ? data.actual_date.toISOString()
        : new Date(data.actual_date).toISOString(),
      note: data.note || '',
      payment_methods: [],
    }),
    queryKeysToInvalidate: ['cashflow', 'general-transactions'],
  },

  RE: {
    code: 'RE',
    label: 'הוצאה חוזרת',
    labelEn: 'Recurring Expense',
    color: 'error',
    icon: 'Repeat',
    category: 'recurring',
    requiresSupplier: false,
    endpoint: '/recurring-transactions/create/',
    fields: [
      { name: 'name', type: 'text', required: true, label: 'שם' },
      { name: 'amount', type: 'number', required: true, label: 'סכום' },
      { name: 'frequency', type: 'select', options: ['weekly', 'monthly'], required: true, label: 'תדירות', default: 'monthly' },
      { name: 'day_of_month', type: 'number', required: false, label: 'יום בחודש', showWhen: { field: 'frequency', value: 'monthly' }, default: 1, min: 1, max: 31 },
      { name: 'start_date', type: 'date', required: true, label: 'תאריך התחלה', default: () => new Date() },
      { name: 'tag_id', type: 'tag', tagType: 'expense', required: false, label: 'קטגוריה' },
    ],
    buildPayload: (data) => ({
      name: data.name,
      type: 'expense',
      tag_id: data.tag_id || null,
      amount: parseFloat(data.amount),
      frequency: data.frequency || 'monthly',
      day_of_month: data.frequency === 'monthly' ? (parseInt(data.day_of_month) || 1) : null,
      start_date: data.start_date instanceof Date
        ? format(data.start_date, 'yyyy-MM-dd')
        : data.start_date,
      end_date: null,
      default_payment_method: 'cash',
      note: '',
    }),
    queryKeysToInvalidate: ['recurring-transactions'],
  },

  RI: {
    code: 'RI',
    label: 'הכנסה חוזרת',
    labelEn: 'Recurring Income',
    color: 'success',
    icon: 'Repeat',
    category: 'recurring',
    requiresSupplier: false,
    endpoint: '/recurring-transactions/create/',
    fields: [
      { name: 'name', type: 'text', required: true, label: 'שם' },
      { name: 'amount', type: 'number', required: true, label: 'סכום' },
      { name: 'frequency', type: 'select', options: ['weekly', 'monthly'], required: true, label: 'תדירות', default: 'monthly' },
      { name: 'day_of_month', type: 'number', required: false, label: 'יום בחודש', showWhen: { field: 'frequency', value: 'monthly' }, default: 1, min: 1, max: 31 },
      { name: 'start_date', type: 'date', required: true, label: 'תאריך התחלה', default: () => new Date() },
      { name: 'tag_id', type: 'tag', tagType: 'income', required: false, label: 'קטגוריה' },
    ],
    buildPayload: (data) => ({
      name: data.name,
      type: 'income',
      tag_id: data.tag_id || null,
      amount: parseFloat(data.amount),
      frequency: data.frequency || 'monthly',
      day_of_month: data.frequency === 'monthly' ? (parseInt(data.day_of_month) || 1) : null,
      start_date: data.start_date instanceof Date
        ? format(data.start_date, 'yyyy-MM-dd')
        : data.start_date,
      end_date: null,
      default_payment_method: 'cash',
      note: '',
    }),
    queryKeysToInvalidate: ['recurring-transactions'],
  },

  CC: {
    code: 'CC',
    label: 'חיוב לקוח',
    labelEn: 'Client Charge',
    color: 'warning',
    icon: 'PersonAdd',
    category: 'client',
    requiresClient: true,
    endpoint: '/client/charge/',
    fields: [
      { name: 'client_id', type: 'client', required: true, label: 'לקוח' },
      { name: 'amount', type: 'number', required: true, label: 'סכום' },
      { name: 'debt_field', type: 'debt_field', required: true, label: 'קטגוריה' },
      { name: 'actual_date', type: 'date', required: true, label: 'תאריך', default: () => new Date() },
      { name: 'note', type: 'text', required: false, label: 'הערה' },
    ],
    buildPayload: (data) => ({
      business_id: data.client_id,
      amount: parseFloat(data.amount),
      actual_date: data.actual_date instanceof Date
        ? data.actual_date.toISOString()
        : new Date(data.actual_date).toISOString(),
      notes: data.note || '',
      field: data.debt_field,
    }),
    queryKeysToInvalidate: ['cashflow', 'clients', 'transactions'],
  },

  CP: {
    code: 'CP',
    label: 'תשלום מלקוח',
    labelEn: 'Client Payment',
    color: 'info',
    icon: 'PersonRemove',
    category: 'client',
    requiresClient: true,
    endpoint: '/client/payment/',
    fields: [
      { name: 'client_id', type: 'client', required: true, label: 'לקוח' },
      { name: 'amount', type: 'number', required: true, label: 'סכום' },
      { name: 'debt_field', type: 'debt_field', required: true, label: 'קטגוריה' },
      { name: 'actual_date', type: 'date', required: true, label: 'תאריך', default: () => new Date() },
      { name: 'note', type: 'text', required: false, label: 'הערה' },
    ],
    buildPayload: (data) => {
      const amount = parseFloat(data.amount);
      const payment_seperation = data.debt_field ? { [data.debt_field]: amount } : {};
      return {
        business_id: data.client_id,
        amount,
        actual_date: data.actual_date instanceof Date
          ? data.actual_date.toISOString()
          : new Date(data.actual_date).toISOString(),
        notes: data.note || '',
        payment_seperation,
      };
    },
    queryKeysToInvalidate: ['cashflow', 'clients', 'transactions'],
  },

  CD: {
    code: 'CD',
    label: 'דיווח קופה',
    labelEn: 'Cash Desk Submission',
    color: 'success',
    icon: 'PointOfSale',
    category: 'cashdesk',
    requiresSupplier: false,
    endpoint: '/cashdesk-submission/create/',
    fields: [
      { name: 'cash_desk_id', type: 'cashdesk', required: true, label: 'קופה' },
      { name: 'period_type', type: 'select', options: ['daily', 'monthly'], required: true, label: 'סוג תקופה', default: 'daily' },
      { name: 'amount', type: 'number', required: true, label: 'סכום', min: 0.01 },
      { name: 'actual_date', type: 'date', required: false, label: 'תאריך', default: () => new Date() },
      { name: 'employee_expenses', type: 'number', required: false, label: 'הוצאות עובדים', min: 0 },
      { name: 'note', type: 'text', required: false, label: 'הערה' },
    ],
    buildPayload: (data) => ({
      CashDeskID: data.cash_desk_id,
      PeriodType: data.period_type || 'daily',
      PeriodDate: data.actual_date instanceof Date
        ? format(data.actual_date, 'yyyy-MM-dd')
        : data.actual_date
          ? format(new Date(data.actual_date), 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd'),
      Amount: parseFloat(data.amount),
      EmployeeExpenses: data.employee_expenses ? parseFloat(data.employee_expenses) : null,
      Note: data.note || '',
      payment_methods: [{ type: 'cash', amount: parseFloat(data.amount) }],
    }),
    queryKeysToInvalidate: ['cashdesk-submissions', 'cashdesk-metrics', 'cashflow', 'cashflow-summary', 'general-transactions'],
  },
};

// Period type display for CD command
export const PERIOD_TYPE_OPTIONS = {
  daily: 'יומי',
  monthly: 'חודשי',
};

// Helper functions
export const getCommandConfig = (code) => COMMAND_TYPES[code];
export const getAllCommandTypes = () => Object.keys(COMMAND_TYPES);
export const getCommandsByCategory = (category) =>
  Object.values(COMMAND_TYPES).filter((cmd) => cmd.category === category);

// Get all command types (always returns all available commands)
export const getEnabledCommandTypes = () => Object.keys(COMMAND_TYPES);

// Frequency options for display
export const FREQUENCY_OPTIONS = {
  weekly: 'שבועי',
  monthly: 'חודשי',
};
